import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatNumber(value) {
  if (value === null || value === undefined) return "";
  
  // Handle objects and other types safely
  let stringValue;
  if (typeof value === "number") {
    stringValue = String(value);
  } else if (typeof value === "string") {
    stringValue = value;
  } else {
    // For objects, try to convert safely
    try {
      if (value && typeof value.toString === "function") {
        stringValue = value.toString();
      } else {
        stringValue = JSON.stringify(value);
      }
    } catch {
      return "";
    }
  }
  
  // Parse the string to number
  const normalized = Number(stringValue.replace(/,/g, "").trim());
  if (Number.isNaN(normalized)) {
    return stringValue || "";
  }
  
  // Format the number
  try {
    return normalized.toLocaleString("en-US");
  } catch {
    return String(normalized);
  }
}

/**
 * Check if current time is within trading hours
 * @param {string} startTime Trading start time in HH:mm format (e.g., "11:00")
 * @param {string} endTime Trading end time in HH:mm format (e.g., "20:00")
 * @returns {boolean} true if current time is within trading hours, false otherwise
 */
export function isWithinTradingHours(startTime, endTime) {
  if (!startTime || !endTime) return true; // If no time set, allow trading
  
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeMinutes = currentHour * 60 + currentMinute;

  // Parse start and end times
  const [startHour, startMinute] = String(startTime).split(':').map(Number);
  const [endHour, endMinute] = String(endTime).split(':').map(Number);
  
  if (isNaN(startHour) || isNaN(endHour)) return true; // If invalid time, allow trading
  
  const startTimeMinutes = startHour * 60 + (startMinute || 0);
  const endTimeMinutes = endHour * 60 + (endMinute || 0);

  return currentTimeMinutes >= startTimeMinutes && currentTimeMinutes <= endTimeMinutes;
}

/**
 * Convert number to Persian/Farsi text representation
 * @param {number} num - The number to convert
 * @param {string} suffix - Optional suffix to append (default: " تومان")
 * @returns {string} Persian text representation of the number
 * 
 * @example
 * numberToPersianText(4453000) // "چهار میلیون و چهارصد و پنجاه و سه هزار تومان"
 * numberToPersianText(1000) // "یک هزار تومان"
 * numberToPersianText(123456) // "یکصد و بیست و سه هزار و چهارصد و پنجاه و شش تومان"
 * numberToPersianText(1080875811) // "یک میلیارد و هشتاد میلیون و هشتصد و هفتاد و پنج هزار و هشتصد و یازده تومان"
 */
export function numberToPersianText(num, suffix = " تومان") {
  if (!num || num === 0) return "";
  
  // Round to integer to avoid floating point issues
  num = Math.round(num);
  
  const ones = [
    "", "یک", "دو", "سه", "چهار", "پنج", "شش", "هفت", "هشت", "نه",
    "ده", "یازده", "دوازده", "سیزده", "چهارده", "پانزده", "شانزده", "هفده", "هجده", "نوزده"
  ];
  
  const tens = [
    "", "", "بیست", "سی", "چهل", "پنجاه", "شصت", "هفتاد", "هشتاد", "نود"
  ];
  
  const hundreds = [
    "", "صد", "دویست", "سیصد", "چهارصد", "پانصد", "ششصد", "هفتصد", "هشتصد", "نهصد"
  ];
  
  const convertGroup = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const one = n % 10;
      return tens[ten] + (one > 0 ? " و " + ones[one] : "");
    }
    if (n < 1000) {
      const hundred = Math.floor(n / 100);
      const remainder = n % 100;
      return hundreds[hundred] + (remainder > 0 ? " و " + convertGroup(remainder) : "");
    }
    return "";
  };
  
  const convert = (n) => {
    if (n === 0) return "صفر";
    
    // Handle billions (میلیارد)
    const billion = Math.floor(n / 1000000000);
    const remainderAfterBillion = n % 1000000000;
    
    // Handle millions (میلیون)
    const million = Math.floor(remainderAfterBillion / 1000000);
    const remainderAfterMillion = remainderAfterBillion % 1000000;
    
    // Handle thousands (هزار)
    const thousand = Math.floor(remainderAfterMillion / 1000);
    const hundred = remainderAfterMillion % 1000;
    
    let result = "";
    
    if (billion > 0) {
      result += convertGroup(billion) + " میلیارد";
    }
    
    if (million > 0) {
      if (result) result += " و ";
      result += convertGroup(million) + " میلیون";
    }
    
    if (thousand > 0) {
      if (result) result += " و ";
      result += convertGroup(thousand) + " هزار";
    }
    
    if (hundred > 0) {
      if (result) result += " و ";
      result += convertGroup(hundred);
    }
    
    return result;
  };
  
  return convert(num) + suffix;
}

/**
 * Round price to nearest thousand (round last 3 digits to 000)
 * @param {string|number} value - The price value to round
 * @returns {number} Rounded price value
 * 
 * @example
 * roundPriceToThousand(8189015) // 8189000
 * roundPriceToThousand("1,897,089,834") // 1897089000
 * roundPriceToThousand(125043) // 125000
 */
export function roundPriceToThousand(value) {
  if (value === null || value === undefined) return 0;
  
  let num = 0;
  if (typeof value === 'number') {
    num = value;
  } else if (typeof value === 'string') {
    // Remove commas and spaces, handle Persian digits
    const cleaned = value.replace(/[،,\s]/g, '').replace(/[۰-۹]/g, (char) => {
      const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
      const englishDigits = '0123456789';
      const index = persianDigits.indexOf(char);
      return index !== -1 ? englishDigits[index] : char;
    });
    num = parseFloat(cleaned) || 0;
  }
  
  // Round to nearest thousand (round last 3 digits to 000)
  return Math.round(num / 1000) * 1000;
}

/** Fixed divisor in amount formula: مظنه / 705 / 4.6083 × karat × وزن */
export const KARAT_AMOUNT_DIVISOR = 705;
export const STANDARD_MITHQAL_TO_GRAM = 4.6083;
export const AB_SHODE_FARDAI_MITHQAL_TO_GRAM = 4.3318;

/** isSys products always use 4.6083 per formula: مظنه/705/4.6083×کارات */
export function getMithqalToGramFactor({ productName = '', isSys = null } = {}) {
  if (isTruthySysFlag(isSys)) return STANDARD_MITHQAL_TO_GRAM;
  if (productName && productName.includes('آب شده فردایی')) return AB_SHODE_FARDAI_MITHQAL_TO_GRAM;
  return STANDARD_MITHQAL_TO_GRAM;
}

/** Resolve karat from API field or product name (e.g. «آبشده 760» → 760) */
export function resolveProductKarat(product) {
  const fromApi = Number(product?.karat);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  const name = String(product?.name || "");
  const match = name.match(/(\d{3})\s*$/);
  if (match) {
    const fromName = Number(match[1]);
    if (Number.isFinite(fromName) && fromName > 0) return fromName;
  }
  return null;
}

export function getEffectiveKarat(karat) {
  const k = Number(karat);
  return Number.isFinite(k) && k > 0 ? k : KARAT_AMOUNT_DIVISOR;
}

/** amount = baseAmount × karat / 705 */
export function applyKaratToAmount(baseAmount, karat) {
  return (baseAmount * getEffectiveKarat(karat)) / KARAT_AMOUNT_DIVISOR;
}

/** Reverse karat factor when deriving base amount from total */
export function removeKaratFromAmount(amount, karat) {
  return (amount * KARAT_AMOUNT_DIVISOR) / getEffectiveKarat(karat);
}

/** Normalize API boolean flags (true, "true", 1) */
export function isTruthySysFlag(isSys) {
  return isSys === true || isSys === 'true' || isSys === 1;
}

/** Karat ratio applies when product is a system product (isSys) */
export function shouldApplyKaratRatio({ isSys }) {
  return isTruthySysFlag(isSys);
}

export function applyKaratToAmountIfNeeded(baseAmount, karat, { unit, isSys }) {
  return shouldApplyKaratRatio({ unit, isSys })
    ? applyKaratToAmount(baseAmount, karat)
    : baseAmount;
}

export function removeKaratFromAmountIfNeeded(amount, karat, { unit, isSys }) {
  return shouldApplyKaratRatio({ unit, isSys })
    ? removeKaratFromAmount(amount, karat)
    : amount;
}

/**
 * Weight-based amount: unitPrice × weight / mithqalFactor × karat / 705 (when isSys)
 */
export function computeWeightProductAmount({ unitPrice, weight, karat, mithqalToGramFactor, unit = null, isSys = null }) {
  const base = (unitPrice * weight) / mithqalToGramFactor;
  return Math.round(applyKaratToAmountIfNeeded(base, karat, { unit, isSys }));
}

/**
 * Derive weight from amount: amount × mithqalFactor / unitPrice (× 705 / karat when isSys)
 */
export function computeWeightFromAmount({ amount, unitPrice, karat, mithqalToGramFactor, unit = null, isSys = null }) {
  if (!unitPrice || unitPrice <= 0) return 0;
  const baseAmount = removeKaratFromAmountIfNeeded(amount, karat, { unit, isSys });
  return (baseAmount * mithqalToGramFactor) / unitPrice;
}

